/**
 * Discovery list-scroll + map-viewport restore.
 *
 * Mirrors shops directory sessionStorage (see shopMapViewModel MAP_VIEW_* keys):
 * session-only, best-effort, never URL. Map camera lat/lng live here — not GPS
 * query params. User GPS must not be persisted (§6); nearby uses a coarse intent
 * flag only and re-acquires position in component state on remount.
 */

import { DEFAULT_MAP_ZOOM, validLatLng } from '../shops/shopMapUtils.js';

/** Window list scroll — scoped to Nakupovat discovery browse. */
export const DISCOVERY_SCROLL_SESSION_KEY = 'customer:discovery:scroll' as const;

/** Browse URL search (`q/filter/mode`) snapshot for durable back restore. */
export const DISCOVERY_BROWSE_SEARCH_SESSION_KEY =
  'customer:discovery:browseSearch' as const;

/** Map camera (center + zoom) — not user GPS; never copied to the URL. */
export const DISCOVERY_MAP_VIEWPORT_SESSION_KEY =
  'customer:discovery:mapViewport' as const;

/** Coarse nearby-intent flag only — never exact lat/lng (§6). */
export const DISCOVERY_NEARBY_CONTEXT_SESSION_KEY =
  'customer:discovery:nearbyContext' as const;

const LEAFLET_ZOOM_MIN = 1;
const LEAFLET_ZOOM_MAX = 22;

export type DiscoveryListScrollState = {
  readonly scrollY: number;
};

export type DiscoveryBrowseSearchState = {
  readonly search: string;
};

export type DiscoveryMapViewportState = {
  readonly centerLat: number;
  readonly centerLng: number;
  readonly zoom: number;
};

/**
 * Manual město/PSČ/oblast pick (Nominatim) — session-only centroid + coarse label.
 * Not device GPS; not a shipping address; never copied to the shareable URL (H04/H05).
 */
export type DiscoveryNearbyManualPlace = {
  readonly lat: number;
  readonly lng: number;
  readonly label: string;
};

/**
 * Coarse “nearby was active this session” signal.
 * GPS: exact device coords must not be stored — remount warm-reacquires.
 * Manual place: session may restore Nominatim centroid + label (H05) without GPS.
 */
export type DiscoveryNearbyContextState = {
  readonly active: true;
  readonly source?: 'gps' | 'manual';
  readonly manualPlace?: DiscoveryNearbyManualPlace;
};

function getSessionStorage(): Storage | null {
  if (
    typeof window === 'undefined' ||
    typeof window.sessionStorage === 'undefined'
  ) {
    return null;
  }
  return window.sessionStorage;
}

function readSessionJson(key: string): unknown | null {
  const storage = getSessionStorage();
  if (storage === null) {
    return null;
  }
  try {
    const raw = storage.getItem(key);
    if (raw === null || raw.trim().length === 0) {
      return null;
    }
    return JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
}

function writeSessionJson(key: string, value: unknown): void {
  const storage = getSessionStorage();
  if (storage === null) {
    return;
  }
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // private mode / quota — restore is best-effort
  }
}

function clearSessionKey(key: string): void {
  const storage = getSessionStorage();
  if (storage === null) {
    return;
  }
  try {
    storage.removeItem(key);
  } catch {
    // ignore
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function parseDiscoveryListScrollState(
  raw: unknown,
): DiscoveryListScrollState | null {
  if (!isRecord(raw)) {
    return null;
  }
  const scrollYRaw = raw.scrollY ?? raw.scrollTop;
  if (typeof scrollYRaw !== 'number' || !Number.isFinite(scrollYRaw)) {
    return null;
  }
  const scrollY = Math.max(0, Math.floor(scrollYRaw));
  return { scrollY };
}

export function parseDiscoveryBrowseSearchState(
  raw: unknown,
): DiscoveryBrowseSearchState | null {
  if (!isRecord(raw)) {
    return null;
  }
  if (typeof raw.search !== 'string') {
    return null;
  }
  const search = raw.search.trim();
  if (search.length === 0 || !search.startsWith('?') || search.length > 512) {
    return null;
  }
  return { search };
}

export function parseDiscoveryMapViewportState(
  raw: unknown,
): DiscoveryMapViewportState | null {
  if (!isRecord(raw)) {
    return null;
  }
  let centerLat: number | null = null;
  if (typeof raw.centerLat === 'number') {
    centerLat = raw.centerLat;
  } else if (typeof raw.lat === 'number') {
    centerLat = raw.lat;
  }
  let centerLng: number | null = null;
  if (typeof raw.centerLng === 'number') {
    centerLng = raw.centerLng;
  } else if (typeof raw.lng === 'number') {
    centerLng = raw.lng;
  }
  if (centerLat === null || centerLng === null) {
    return null;
  }
  if (!validLatLng(centerLat, centerLng)) {
    return null;
  }
  let zoom = DEFAULT_MAP_ZOOM;
  if (raw.zoom !== undefined) {
    if (typeof raw.zoom !== 'number' || !Number.isFinite(raw.zoom)) {
      return null;
    }
    zoom = Math.round(raw.zoom);
    if (zoom < LEAFLET_ZOOM_MIN || zoom > LEAFLET_ZOOM_MAX) {
      return null;
    }
  }
  return {
    centerLat,
    centerLng,
    zoom,
  };
}

function parseManualPlace(
  raw: unknown,
): DiscoveryNearbyManualPlace | null {
  if (!isRecord(raw)) {
    return null;
  }
  if (
    typeof raw.lat !== 'number' ||
    typeof raw.lng !== 'number' ||
    !validLatLng(raw.lat, raw.lng)
  ) {
    return null;
  }
  if (typeof raw.label !== 'string') {
    return null;
  }
  const label = raw.label.trim();
  if (label.length === 0 || label.length > 120) {
    return null;
  }
  return { lat: raw.lat, lng: raw.lng, label };
}

export function parseDiscoveryNearbyContextState(
  raw: unknown,
): DiscoveryNearbyContextState | null {
  if (!isRecord(raw)) {
    return null;
  }
  if (raw.active === true) {
    const source = raw.source === 'manual' || raw.source === 'gps' ? raw.source : undefined;
    if (source === 'manual') {
      const manualPlace = parseManualPlace(raw.manualPlace);
      if (manualPlace !== null) {
        return { active: true, source: 'manual', manualPlace };
      }
      // Manual without restoreable place → coarse intent only (no GPS surprise).
      return { active: true, source: 'manual' };
    }
    if (source === 'gps') {
      return { active: true, source: 'gps' };
    }
    return { active: true };
  }
  // Legacy exact-GPS payloads (§6): accept as coarse intent only — never restore coords.
  if (
    typeof raw.lat === 'number' &&
    typeof raw.lng === 'number' &&
    validLatLng(raw.lat, raw.lng)
  ) {
    return { active: true };
  }
  return null;
}

export function readDiscoveryListScroll(): DiscoveryListScrollState | null {
  return parseDiscoveryListScrollState(readSessionJson(DISCOVERY_SCROLL_SESSION_KEY));
}

export function writeDiscoveryListScroll(state: DiscoveryListScrollState): void {
  const parsed = parseDiscoveryListScrollState(state);
  if (parsed === null) {
    return;
  }
  writeSessionJson(DISCOVERY_SCROLL_SESSION_KEY, parsed);
}

export function clearDiscoveryListScroll(): void {
  clearSessionKey(DISCOVERY_SCROLL_SESSION_KEY);
}

export function readDiscoveryBrowseSearch(): DiscoveryBrowseSearchState | null {
  return parseDiscoveryBrowseSearchState(
    readSessionJson(DISCOVERY_BROWSE_SEARCH_SESSION_KEY),
  );
}

export function writeDiscoveryBrowseSearch(
  state: DiscoveryBrowseSearchState,
): void {
  const parsed = parseDiscoveryBrowseSearchState(state);
  if (parsed === null) {
    return;
  }
  writeSessionJson(DISCOVERY_BROWSE_SEARCH_SESSION_KEY, parsed);
}

export function clearDiscoveryBrowseSearch(): void {
  clearSessionKey(DISCOVERY_BROWSE_SEARCH_SESSION_KEY);
}

export function readDiscoveryMapViewport(): DiscoveryMapViewportState | null {
  return parseDiscoveryMapViewportState(
    readSessionJson(DISCOVERY_MAP_VIEWPORT_SESSION_KEY),
  );
}

export function writeDiscoveryMapViewport(state: DiscoveryMapViewportState): void {
  const parsed = parseDiscoveryMapViewportState(state);
  if (parsed === null) {
    return;
  }
  writeSessionJson(DISCOVERY_MAP_VIEWPORT_SESSION_KEY, parsed);
}

export function clearDiscoveryMapViewport(): void {
  clearSessionKey(DISCOVERY_MAP_VIEWPORT_SESSION_KEY);
}

export function readDiscoveryNearbyContext(): DiscoveryNearbyContextState | null {
  return parseDiscoveryNearbyContextState(
    readSessionJson(DISCOVERY_NEARBY_CONTEXT_SESSION_KEY),
  );
}

export function writeDiscoveryNearbyContext(
  state: DiscoveryNearbyContextState | null,
): void {
  if (state === null) {
    writeSessionJson(DISCOVERY_NEARBY_CONTEXT_SESSION_KEY, null);
    return;
  }
  const parsed = parseDiscoveryNearbyContextState(state);
  if (parsed === null) {
    return;
  }
  writeSessionJson(DISCOVERY_NEARBY_CONTEXT_SESSION_KEY, parsed);
}

export function clearDiscoveryNearbyContext(): void {
  clearSessionKey(DISCOVERY_NEARBY_CONTEXT_SESSION_KEY);
}
